/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ActionList, InfoCardKernel, InfoCardKernelType } from 'client-code/actions';
import { PreviewGroup, PreviewType, UnifiedCollectionType } from 'client-code/previews';

import liveIcon from '../../../assets/images/hydra_icon_source_live.png';
import diskIcon from '../../../assets/images/hydra_icon_source_on_disk.png';
import { ErrorCodeTypes } from '../../../core/enums';
import { IInfoCardEvents, IInfoCardProperty, InfoCardManager } from '../../../core/infocard';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { CursorManager } from '../../../core/navigation';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import {
    TInfoCardKernel,
    TInfoCardKernelType,
    TOfferPreview,
    TVodOfferPreview,
} from '../../../core/types';
import { InfocardActions, MetadataOperations, PreviewComponent } from '../../../core/usecases';
import { ErrorCodeUtil } from '../../../core/utils/ErrorCodeUtil';
import { setInfoCardGhostModeAction } from '../../../store/infocard/infocard.actions';
import {
    onVisibilityHideInfoCardOperations,
    onVisibilityShowInfoCardOperations,
} from '../../../store/infocard/infocard.reducers';
import { onKeyBackGlobalInfoCard } from '../../../store/infocard/infocardnavigation.reducers';
import { InfocardNavigationConstants } from '../../../store/infocard/InfocardNavigationConstants';
import { showEnterPCPinOverlay } from '../../../store/overlay/pcpinoverlay/pcpinoverlay.reducers';
import { showWhisper } from '../../../store/whisper/whisper.reducer';
import { ImageLoader } from '../../ImageLoader';
import { WhisperTypes } from '../../Whisper/WhisperTypes';
import { Mousetrap } from '../Mousetrap';
import { ActionButton, ContentTitle, Divider } from '../ui-kit';
import { getEpisodeInfoCardContent } from './EpisodeInfoCard';
import type { TFlattenedMetaData, TInfoCardContentUtil, TInfoCardMetaData } from './InfoCardProps';
import { InfoCardUserAction } from './InfoCardUserAction';
import { getMovieInfoCardContent } from './MovieInfoCard';
import { getNonEpisodicInfoCardContent } from './NonEpisodicInfoCard';
import { getPersonInfoCardContent } from './PersonInfoCard';
import { getSeriesInfoCardContent } from './SeriesInfoCard';
import { getTeamAndLeagueInfoCardContent } from './TeamAndLeagueInfoCard';

import './InfoCard.scss';

const InfoCard: FunctionComponent = () => {
    const TAG = 'InfoCardScreen';

    const oInfoCardManagerRef = useRef<InfoCardManager | null>(
        InfoCardManager.getInstance() as InfoCardManager,
    );
    const updateInfoCardScroller = useCallback((element: HTMLDivElement | null) => {
        oInfoCardManagerRef.current?.setInfoCardScrollHandler(
            element?.scrollBy.bind(element) ?? null,
        );
    }, []);

    const [showInfoCard, setShowInfoCard] = useState<boolean>(false);
    const [infoCardKernelType, setInfoCardKernelType] = useState<
        InfoCardKernelType | TInfoCardKernelType | null
    >(null);
    const [infocardProperties, setInfocardProperties] = useState<IInfoCardProperty>(
        InfoCardManager.getInstance().getProperties(),
    );
    const [actionsOptions, setActionsOptions] = useState<InfoCardUserAction[]>([]);
    const [isActionMode, setIsActionMode] = useState<boolean>(false);
    const [hasPreviewError, setHasPreviewError] = useState<boolean>(false);
    const [shouldAskPin, setShouldAskPin] = useState<boolean>(false);
    const [isOnPinChallenge, setIsOnPinChallenge] = useState<boolean>(false);

    const [previewInfo, setPreviewInfo] = useState<TInfoCardMetaData | null>(null);

    const previewGroups: Array<PreviewGroup> = [
        PreviewGroup.Description,
        PreviewGroup.Metadata,
        PreviewGroup.Credits,
    ];

    const dispatch = useTivoDispatch();
    const { highlight, ghostMode, isCursorOverLeftArrow, isCursorOverRightArrow } = useTivoSelector(
        (state: RootState) => ({
            highlight: state.infocard.highlight,
            ghostMode: state.infocard.ghostMode,
            isCursorOverLeftArrow: state.infocard.isCursorOverLeftArrow,
            isCursorOverRightArrow: state.infocard.isCursorOverRightArrow,
        }),
    );

    const getFlattenedMetaData = (prevInfo: TInfoCardMetaData | null): TFlattenedMetaData => {
        const metadata =
            prevInfo?.contentPreviewInfo ||
            prevInfo?.offerPreviewInfo ||
            prevInfo?.recordingPreviewInfo ||
            prevInfo?.unicPreviewInfo ||
            prevInfo?.vodOfferPreviewInfo ||
            prevInfo?.infoCardPersonPreview ||
            {};

        return {
            ...metadata,
            ...((metadata as TOfferPreview)?.offerPreview || {}),
            ...(((metadata as TOfferPreview)?.offerPreview || {}).contentPreview || {}),
            ...((metadata as TVodOfferPreview)?.vodOfferPreview || {}),
            ...(((metadata as TVodOfferPreview)?.vodOfferPreview || {}).contentPreview || {}),
        };
    };

    const getInfoCardContent = (): ReturnType<TInfoCardContentUtil> => {
        if (hasPreviewError)
            return {
                headerContent: <React.Fragment></React.Fragment>,
                bodyContent: (
                    <ContentTitle title={Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE)} />
                ),
                headerClassName: 'banner-only',
            };
        const flattenedMetadata = getFlattenedMetaData(previewInfo);
        const { previewType, unifiedCollectionType } = flattenedMetadata;

        if (previewType === PreviewType.EpisodePreview)
            return getEpisodeInfoCardContent(flattenedMetadata);
        if (previewType === PreviewType.MoviePreview)
            return getMovieInfoCardContent(flattenedMetadata);
        if (previewType === PreviewType.ProgramPreview)
            return getNonEpisodicInfoCardContent(flattenedMetadata);
        if (unifiedCollectionType === UnifiedCollectionType.SeriesPreviewV2)
            return getSeriesInfoCardContent(flattenedMetadata);
        if (
            unifiedCollectionType === UnifiedCollectionType.TeamPreview ||
            unifiedCollectionType === UnifiedCollectionType.LeaguePreview
        )
            return getTeamAndLeagueInfoCardContent(flattenedMetadata);
        if (flattenedMetadata.infoCardKernelType === TInfoCardKernelType.TPersonInfoCardKernel)
            return getPersonInfoCardContent(flattenedMetadata);

        return {
            headerContent: <React.Fragment></React.Fragment>,
            headerClassName: '',
            bodyContent: <React.Fragment></React.Fragment>,
        };
    };

    const getIcon = (icon: string): string => {
        if (icon === 'liveIcon') return liveIcon;
        if (icon === 'diskIcon') return diskIcon;
        return icon;
    };

    const narrateInfoCard = (): JSX.Element | null => {
        if (!showInfoCard || ((!infoCardKernelType || !previewInfo) && !hasPreviewError))
            return null;

        if (shouldAskPin && !isOnPinChallenge) {
            return null;
        }

        const {
            headerContent,
            headerClassName = '',
            bodyContent,
            bodyClassName = '',
        } = getInfoCardContent();

        const iconsToShowInDetailMode = actionsOptions.find(
            (action) => action.id === 'WATCH_NOW',
        )?.icons;

        return (
            <div
                className="info-card-container"
                style={{ visibility: isOnPinChallenge ? 'hidden' : 'visible' }}
            >
                {/* <div className="info-card metadata-only"> 
                to show metadata only in scroll mode */}
                <Mousetrap
                    value={InfocardNavigationConstants.INFO_CARD_SHIELD}
                    className="info-card"
                />
                <div className="info-card">
                    <Mousetrap
                        value={InfocardNavigationConstants.INFO_CARD_WEST}
                        className={
                            infocardProperties.hasItemsBefore
                                ? `left-nav-arrow ${isCursorOverLeftArrow ? 'left-nav-arrow-highlight' : ''}`
                                : 'left-nav-arrow hidden'
                        }
                    />
                    <Mousetrap
                        value={InfocardNavigationConstants.INFO_CARD_EAST}
                        className={
                            infocardProperties.hasItemsAfter
                                ? `right-nav-arrow ${isCursorOverRightArrow ? 'right-nav-arrow-highlight' : ''}`
                                : 'right-nav-arrow hidden'
                        }
                    />
                    <div ref={updateInfoCardScroller} className="info-card-content">
                        {/* <div className="showcase-banner-metadata">
                        to switch between banner with metadata view */}
                        <div
                            className={`info-card-header showcase-banner-metadata${headerClassName ? ` ${headerClassName}` : ''}`}
                        >
                            {headerContent}
                        </div>

                        <div
                            className={`info-card-body${bodyClassName ? ` ${bodyClassName}` : ''}`}
                        >
                            {bodyContent}
                        </div>
                    </div>

                    <div className="info-card-footer">
                        <Divider />
                        {!isActionMode && (
                            <ActionButton index={0} highlighted={true}>
                                {'Options'}
                                {iconsToShowInDetailMode && (
                                    <div className="infocard-action-icons">
                                        {iconsToShowInDetailMode.map((icon, iconIdx) => (
                                            <ImageLoader key={iconIdx} src={getIcon(icon)} />
                                        ))}
                                    </div>
                                )}
                            </ActionButton>
                        )}
                        {isActionMode &&
                            (actionsOptions.length > 0 ? (
                                <>
                                    {actionsOptions.map((userAction, idx) => (
                                        <ActionButton
                                            key={userAction.id}
                                            index={idx}
                                            highlighted={idx === highlight}
                                            ghostmode={ghostMode}
                                        >
                                            {userAction.text}
                                            {userAction.icons && (
                                                <div className="infocard-action-icons">
                                                    {userAction.icons.map((icon, iconIdx) => (
                                                        <ImageLoader
                                                            key={iconIdx}
                                                            src={getIcon(icon)}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </ActionButton>
                                    ))}
                                    <ActionButton
                                        type="info"
                                        index={actionsOptions.length}
                                        highlighted={highlight === actionsOptions.length}
                                        ghostmode={ghostMode}
                                    />
                                </>
                            ) : (
                                <ActionButton type="info" index={0} highlighted={true} />
                            ))}
                    </div>
                </div>
            </div>
        );
    };

    const handleOnInfoCardKernelChanged = async (infoCardKernel: InfoCardKernel | null) => {
        setHasPreviewError(false);
        setActionsOptions([]);
        setShouldAskPin(false);
        setIsOnPinChallenge(false);

        if (infoCardKernel) {
            const [processInfoCardResponse, actionsOptionsResponse] = await Promise.all([
                processInfoCardKernel(infoCardKernel)
                    .then((value: void) => ({ status: 'fulfilled' as const, value }))
                    .catch((reason: unknown) => ({ status: 'rejected' as const, reason })),
                getActionsOptions(infoCardKernel)
                    .then((value: void) => ({ status: 'fulfilled' as const, value }))
                    .catch((reason: unknown) => ({ status: 'rejected' as const, reason })),
            ]);

            const failA = processInfoCardResponse.status === 'rejected';
            const failB = actionsOptionsResponse.status === 'rejected';

            if (failA && failB) {
                Diagnostics.error(
                    TAG,
                    `${processInfoCardResponse.reason instanceof Error ? processInfoCardResponse.reason.message : 'Process InfoCard Kernel Preview Error'}
                    \n\n${actionsOptionsResponse.reason instanceof Error ? actionsOptionsResponse.reason.message : 'Get Actions Options Error'}`,
                    new Error('Both InfoCard data and actions failed to load.'),
                );
                dispatch(onKeyBackGlobalInfoCard());

                const errorMessageToShow = Locale.getString(
                    LocaleKeys.WHISPER_SCREEN_DETAILS_UNAVAILABLE,
                ).replace(
                    'ERROR_CODE',
                    ErrorCodeUtil.getSuffixFromErrorType(ErrorCodeTypes.SCHEMA_EXCEPTION),
                );

                dispatch(
                    showWhisper({
                        message: errorMessageToShow,
                        iconType: WhisperTypes.NO_ICON,
                    }),
                );
                return;
            }

            if (failA) {
                setHasPreviewError(true);
            }
        } else {
            setInfoCardKernelType(null);
            setPreviewInfo(null);
        }
    };

    const handleOnInfoCardPropertiesChanged = (properties: IInfoCardProperty) => {
        setInfocardProperties(properties);
        setIsActionMode(properties.actionButtonVisibility);
    };

    const handleOnInfoCardVisibilityChanged = (isVisible: boolean) => {
        setShowInfoCard(isVisible);
        if (isVisible) {
            dispatch(onVisibilityShowInfoCardOperations());
        } else {
            dispatch(onVisibilityHideInfoCardOperations());
        }
    };

    const getActionsOptions = async (aInfoCardKernel: InfoCardKernel) => {
        const useCases = new InfocardActions(aInfoCardKernel);
        const metadataOperations = new MetadataOperations();

        let actions: ActionList = { actions: [] };
        let infoCardKernelMetadata: InfoCardUserAction[] = [];

        try {
            switch (aInfoCardKernel.infoCardKernelType) {
                case InfoCardKernelType.ContentInfoCardKernel:
                    actions = await useCases.getContentActionsInfoCard();
                    break;

                case InfoCardKernelType.OfferInfoCardKernel:
                    actions = await useCases.getOfferActionsInfoCard();
                    break;

                case InfoCardKernelType.RecordingInfoCardKernel:
                    actions = await useCases.getRecordingActionsInfoCard();
                    break;

                case InfoCardKernelType.UnifiedCollectionInfoCardKernel:
                    actions = await useCases.getUnifiedCollectionActionsInfoCard();
                    break;

                case InfoCardKernelType.VodOfferInfoCardKernel:
                    actions = await useCases.getVodActionsInfoCard();
                    break;

                default:
            }
            infoCardKernelMetadata = metadataOperations.getMetaDataForInfoCardActionsOptions(
                actions,
                aInfoCardKernel.infoCardKernelType,
            );
            const sharedPrefMetadata = new SharedPreferences(
                SharedPrefConstants.INFO_CARD_PREF_KEY,
            ).edit();
            sharedPrefMetadata.putJson(
                SharedPrefConstants.INFO_CARD_OPTIONS,
                infoCardKernelMetadata as unknown as JSON,
            );
            sharedPrefMetadata.apply();
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Get Actions Options Error';
            Log.e(TAG, errorMessage);
            Diagnostics.error(TAG, errorMessage, error);
            infoCardKernelMetadata = [];
            throw new Error('Failed to get InfoCard actions options.');
        }
        const actionCount = infoCardKernelMetadata.length;
        oInfoCardManagerRef.current?.setProperties({
            actionButtonCount: actionCount,
        });
        setActionsOptions(infoCardKernelMetadata);
    };

    const processInfoCardKernel = async (aInfoCardKernel: InfoCardKernel | TInfoCardKernel) => {
        setInfoCardKernelType(aInfoCardKernel.infoCardKernelType);
        const useCases = new PreviewComponent(aInfoCardKernel);

        try {
            let res: TInfoCardMetaData | null = null;
            switch (aInfoCardKernel.infoCardKernelType) {
                case InfoCardKernelType.ContentInfoCardKernel:
                    res = { contentPreviewInfo: await useCases.getContentPreviewV3Info() };
                    break;
                case InfoCardKernelType.InfoCardKernel:
                    // Handle info card mode
                    break;
                case InfoCardKernelType.OfferInfoCardKernel:
                    res = { offerPreviewInfo: await useCases.getOfferPreviewInfo() };
                    break;
                case InfoCardKernelType.RecordingInfoCardKernel:
                    res = {
                        recordingPreviewInfo: await useCases.getRecordingPreviewInfo(previewGroups),
                    };
                    break;
                case InfoCardKernelType.UnifiedCollectionInfoCardKernel:
                    res = {
                        unicPreviewInfo:
                            await useCases.getUnifiedCollectionPreviewInfo(previewGroups),
                    };
                    break;
                case InfoCardKernelType.VodOfferInfoCardKernel:
                    res = {
                        vodOfferPreviewInfo: await useCases.getVodOfferPreviewV2Info(previewGroups),
                    };
                    break;
                case TInfoCardKernelType.TPersonInfoCardKernel:
                    res = { infoCardPersonPreview: await useCases.getPersonPreviewInfo() };
                    break;
                default:
                    break;
            }

            if (res) {
                const askPin =
                    !!getFlattenedMetaData(res).isAdult &&
                    new ServicesClientImpl().shouldHideMetadata();
                setShouldAskPin(askPin);
            }

            setPreviewInfo(res);
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Process InfoCard Kernel Preview Error';
            Log.e(TAG, errorMessage);
            Diagnostics.error(TAG, errorMessage, error);
            throw new Error('Failed to process InfoCard Kernel.');
        }
    };

    const subscribeInfoCardEvents = () => {
        if (oInfoCardManagerRef.current) {
            const oInfoCardEvents: IInfoCardEvents = oInfoCardManagerRef.current;
            oInfoCardEvents.onInfoCardKernelChanged(handleOnInfoCardKernelChanged);
            oInfoCardEvents.onInfoCardVisibilityChanged(handleOnInfoCardVisibilityChanged);
            oInfoCardEvents.onInfoCardPropertyChanged(handleOnInfoCardPropertiesChanged);
        }
    };

    const onCursorStateChangeCallback = (state) => {
        if (!state) dispatch(setInfoCardGhostModeAction(false));
        else dispatch(setInfoCardGhostModeAction(true));
    };

    useEffect(() => {
        if (oInfoCardManagerRef.current) {
            subscribeInfoCardEvents();
        }
        const ref = CursorManager.subscribe(onCursorStateChangeCallback);
        return () => {
            CursorManager.unsubscribe(ref);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (shouldAskPin && !isOnPinChallenge) {
            setIsOnPinChallenge(true);
            dispatch(
                showEnterPCPinOverlay(() => {
                    setIsOnPinChallenge(false);
                    setShouldAskPin(false);
                }),
            );
        }
    }, [shouldAskPin, isOnPinChallenge]);

    return (
        <React.Fragment>
            {oInfoCardManagerRef.current?.isInfoCardVisible() && (
                <Mousetrap
                    value={InfocardNavigationConstants.INFO_CARD_GLOBAL_MOUSE}
                    className="info-card-global-mouse-trap"
                />
            )}
            {narrateInfoCard()}
        </React.Fragment>
    );
};

export default InfoCard;
